/** Kinds whose control is a text-like input, so lengths and patterns mean something. */
const TEXTUAL = new Set(["text", "email", "password", "textarea"]);
/** Kinds whose control is numeric, so bounds and step mean something. */
const NUMERIC = new Set(["number", "slider"]);
/**
 * The track a slider is drawn on, given what bounds it and what it holds.
 *
 * A slider has to span something to be drawn at all, and where nothing states a range it takes the
 * one a bare `<input type="range">` assumes. That default is the kind's, not a rule — and a default
 * is not a licence to misrepresent: a form holding `150` with no bound declared drew a track ending
 * at `100` and put the thumb there, so the page showed a number the form did not hold, with nothing
 * said. Both renderers had invented the same `?? 100` separately, which is how they came to agree
 * about a lie.
 *
 * **Widened only where nothing was declared.** A document that *did* declare `max: 50` keeps it:
 * the attribute is the native guard, it must not promise less than the rules it came from
 * (`VAL-004`), and since [ADR 0066](../../../docs/architecture/0066-a-bound-beside-the-field-is-a-rule.md)
 * a value past it is refused with a message — so the page explains the difference instead of hiding
 * it. Where nothing was declared there is no rule to explain anything, and showing the value is the
 * only honest answer left.
 *
 * **`step` is dropped where it would move the thumb.** The platform snaps a range input to a
 * multiple, so a value of `7` with `step: 5` was drawn at `5`. There is no step rule to appeal to —
 * the validator vocabulary has none — so the affordance gives way to the value.
 */
export function sliderTrack(constraints, value) {
    const declaredMin = constraints.min;
    const declaredMax = constraints.max;
    let min = declaredMin ?? 0;
    let max = declaredMax ?? 100;
    const held = typeof value === "number" && Number.isFinite(value) ? value : null;
    if (held !== null && declaredMin === null && held < min)
        min = held;
    if (held !== null && declaredMax === null && held > max)
        max = held;
    const step = constraints.step;
    const onAStep = step === null || step <= 0 || held === null || Number.isInteger((held - min) / step);
    return { min, max, step: onAStep ? step : null };
}
/**
 * What `kind` can carry of `constraints`.
 *
 * Only what the kind's own control understands: a `maxlength` on a number input is ignored by the
 * platform, and offering it would be a promise the widget does not keep. `inputType` is deliberately
 * absent — the kind decides what the input *is*, and a rule may not change it.
 */
export function nativeConstraintAttributes(kind, constraints, 
/** What the field holds, where the kind draws it — a slider's track spans it. */
value) {
    const numeric = NUMERIC.has(kind);
    const textual = TEXTUAL.has(kind);
    const attributes = {};
    if (numeric) {
        // A slider spans a track whether or not anything declared one — see {@link sliderTrack}, which
        // is where that default lives and where it is widened to hold what the field holds.
        const spans = kind === "slider";
        const track = spans ? sliderTrack(constraints, value ?? null) : null;
        const low = track ? track.min : constraints.min;
        const high = track ? track.max : constraints.max;
        const step = track ? track.step : constraints.step;
        attributes["min"] = low === null ? null : String(low);
        attributes["max"] = high === null ? null : String(high);
        attributes["step"] = step === null ? null : String(step);
    }
    if (textual) {
        attributes["minlength"] =
            constraints.minLength === null ? null : String(constraints.minLength);
        attributes["maxlength"] =
            constraints.maxLength === null ? null : String(constraints.maxLength);
        // A textarea has no `pattern`: the platform ignores it there, and a rule that looks enforced and
        // is not is worse than one that plainly is not.
        attributes["pattern"] = kind === "textarea" ? null : constraints.pattern;
        attributes["inputmode"] = constraints.inputMode;
    }
    return attributes;
}
/**
 * What a control offers: the field's rules, narrowed by what the control itself asks for.
 *
 * A control may ask for **less** than the field accepts — a slider drawn over part of its range, a
 * number input a caller capped — and never for more: the rules are the authority, and offering
 * beyond them would invite a value the form is going to refuse. So each end takes whichever is
 * tighter, which is the same rule two validators already follow between themselves.
 */
/**
 * Strings a pattern comparison is tried against.
 *
 * Fixed and small on purpose: what this buys is a **counterexample**, and one is enough. Absence of
 * one is not a proof that a control's pattern is tighter — it is the honest limit of comparing two
 * regular expressions without deciding language containment, which is not something a render path
 * can afford to attempt.
 */
const PATTERN_PROBES = Object.freeze([
    "", " ", "a", "ab", "abc", "abcd", "abcdefgh", "ABCD", "0", "01234", "a b", "xax", "-", "a-b", "é",
]);
/**
 * The pattern a control ends up offering.
 *
 * A control may ask for **less** than the field accepts and never for more, and a pattern is the one
 * constraint with no order between two of them: there is no expression that means "both" without
 * writing it. So the control's own is taken unless it can be *shown* to loosen — a probe the rules
 * refuse and the control's pattern accepts is that proof, and `^.*$` over `^[a-z]{4,}$` produces one
 * on the first string.
 *
 * Where no counterexample turns up, the control's pattern is taken: a control asking for a stricter
 * spelling of the same rule is the ordinary reason to narrow at all.
 */
function narrowedPattern(rules, offered) {
    if (offered === null)
        return rules;
    if (rules === null || rules === offered)
        return offered;
    let ruled;
    let control;
    try {
        ruled = new RegExp(rules);
        control = new RegExp(offered);
    }
    catch {
        // One of them is not a pattern the platform can read; the rules are the authority.
        return rules;
    }
    const loosens = PATTERN_PROBES.some((probe) => control.test(probe) && !ruled.test(probe));
    return loosens ? rules : offered;
}
/**
 * The coarser of two steps, and coarser means *contained*.
 *
 * A step is a lattice, not a limit: 4 over a field of 2 offers `0 4 8`, a subset of `0 2 4 6 8`, and
 * 3 over the same field offers `3` and `9`, which the field refuses. Taking the higher number read
 * "asks for less" the way `max` does and it does not hold — a control narrowed to 3 let a person
 * stop on a value its own form rejects.
 *
 * The tightest lattice containing both is the least common multiple: over 2 and 3 it is 6, which
 * offers `0 6 12` and sits inside each. Whole steps only; a fractional one has no lcm to speak of,
 * and there the field's own step stands.
 */
function coarser(rules, offered) {
    if (rules === null)
        return offered ?? null;
    if (offered === null || offered === undefined)
        return rules;
    if (!Number.isInteger(rules) || !Number.isInteger(offered) || rules <= 0 || offered <= 0) {
        return Math.max(rules, offered);
    }
    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    return (rules / gcd(rules, offered)) * offered;
}
export function narrowConstraints(rules, narrowing) {
    if (!narrowing)
        return rules;
    const higher = (a, b) => a === null ? b ?? null : b === null || b === undefined ? a : Math.max(a, b);
    const lower = (a, b) => a === null ? b ?? null : b === null || b === undefined ? a : Math.min(a, b);
    return {
        min: higher(rules.min, narrowing.min),
        max: lower(rules.max, narrowing.max),
        step: coarser(rules.step, narrowing.step),
        minLength: higher(rules.minLength, narrowing.minLength),
        maxLength: lower(rules.maxLength, narrowing.maxLength),
        pattern: narrowedPattern(rules.pattern, narrowing.pattern ?? null),
        inputMode: narrowing.inputMode ?? rules.inputMode,
    };
}
